// utils/invitations.ts

import {
  UserRole,
  InvitationStatus,
  Invitation,
  OrganisationTeamUserResponse,
  OrganisationInvitationResponse,
  OrganisationInvitationCreateRequest,
  InvitationResendResponse,
} from '@fsp/types';
import { apiClient } from './apiClient';

const handleApiError = async (response: Response): Promise<never> => {
  let errorMessage: string;

  try {
    const errorData = await response.json();
    errorMessage = errorData?.message || 'An unexpected error occurred';
  } catch {
    errorMessage = 'An unexpected error occurred';
  }

  if (response.status >= 500) {
    throw new Error('Server error. Please try again later.');
  }

  throw new Error(errorMessage);
};

/**
 * Get current user's role in the organisation
 */
export const getCurrentUserRole = async (
  organisationId: string | null,
  userId: string
): Promise<UserRole> => {
  if (!organisationId) {
    throw new Error('Invalid organisationId ID');
  }

  const response = await apiClient.get(`/organisations/${organisationId}/team/${userId}`);

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('You are not a member of this organization');
    }
    await handleApiError(response);
  }

  const organisationTeamUser: OrganisationTeamUserResponse = await response.json();
  return organisationTeamUser.user.role;
};

/**
 * Fetch all invitations for an organisation
 */
export const fetchInvitations = async (organisationId?: string | null): Promise<Invitation[]> => {
  if (!organisationId) {
    throw new Error('Invalid organisation ID');
  }
  const response = await apiClient.get(`/organisations/${organisationId}/invitations`);

  if (!response.ok) {
    if (response.status === 403) {
      throw new Error('You do not have permission to view invitations');
    }
    await handleApiError(response);
  }

  const result: OrganisationInvitationResponse = await response.json();
  return result.invitations || [];
};

/**
 * Send a new invitation
 */
export const sendInvitation = async (
  email: string,
  role: UserRole,
  organisationId?: string | null
): Promise<void> => {
  if (!organisationId) {
    throw new Error('Invalid organisationId ID');
  }

  // Validate email format
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    throw new Error('Please enter a valid email address');
  }

  const request: OrganisationInvitationCreateRequest = {
    email: email.toLowerCase().trim(),
    role: role,
  };

  const response = await apiClient.post(`/organisations/${organisationId}/invitations`, request);

  if (!response.ok) {
    await handleApiError(response);
  }
};

/**
 * Cancel an invitation
 */
export const cancelInvitation = async (
  invitationId: string,
  organisationId?: string | null
): Promise<void> => {
  if (!organisationId) {
    throw new Error('Invalid organisationId ID');
  }

  const response = await apiClient.delete(
    `/organisations/${organisationId}/invitations/${invitationId}`
  );

  if (!response.ok) {
    await handleApiError(response);
  }
};

/**
 * Check if current user can manage invitations
 */
export const canManageInvitations = (userRole: UserRole | null): boolean => {
  return userRole === UserRole.OWNER || userRole === UserRole.ADMIN;
};

/**
 * Get available roles that the current user can assign
 */
export const getAvailableRoles = (currentUserRole: UserRole): UserRole[] => {
  const roles = [UserRole.VIEWER, UserRole.MEMBER];

  // Admins and owners can assign approver and auditor roles
  if (currentUserRole === UserRole.OWNER || currentUserRole === UserRole.ADMIN) {
    roles.push(UserRole.APPROVER, UserRole.AUDITOR);
  }

  // Only owners can assign admin or owner roles
  if (currentUserRole === UserRole.OWNER) {
    roles.push(UserRole.ADMIN, UserRole.OWNER);
  }

  return roles;
};

/**
 * Check if an invitation has expired
 */
export const isInvitationExpired = (expiresAt: Date | string): boolean => {
  return new Date(expiresAt) < new Date();
};

/**
 * Get the effective status of an invitation (accounting for expiration)
 */
export const getEffectiveStatus = (invitation: Invitation): InvitationStatus => {
  if (isInvitationExpired(invitation.expiresAt) && invitation.status === InvitationStatus.PENDING) {
    return InvitationStatus.EXPIRED;
  }
  return invitation.status;
};

/**
 * Check if an invitation can be cancelled
 */
export const canCancelInvitation = (invitation: Invitation): boolean => {
  return (
    invitation.status === InvitationStatus.PENDING && !isInvitationExpired(invitation.expiresAt)
  );
};

/**
 * Resend an invitation email
 **/
export const resendInvitation = async (token: string): Promise<InvitationResendResponse> => {
  const response = await apiClient.post(`/invitations/${token}/resend`);

  const apiResponse: InvitationResendResponse = await response.json();

  if (!response.ok || !apiResponse.success) {
    throw new Error(apiResponse.message || `Failed to resend invitation: ${response.statusText}`);
  }

  return apiResponse as InvitationResendResponse;
};
